import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import {
  useListBlacklistsQuery,
  useCreateBlacklistMutation,
  useDeleteBlacklistMutation,
} from "~/shared/api/blacklist/blacklist.grpc";

/**
 * A row as shown in the blacklist tab. Rows that only exist in the draft have no
 * `id` yet — they are created when the modal is saved.
 */
export type BlacklistDraftRow = {
  /** Stable key for React and for addressing the row while it is unsaved. */
  key: string;
  /** Server id; absent for rows added in this editing session. */
  id?: bigint;
  truckTypeId: bigint;
  truckTypeName: string;
  reason: string;
  createdAt?: Date;
};

/**
 * Holds the blacklist tab's unsaved edits.
 *
 * The tab stages rather than writing straight through: the dispatcher adds and
 * removes truck types, and nothing reaches the server until they press Save.
 * `flush` applies the difference between the server's entries and the draft —
 * so it is the modal's save handler, not the tab, that decides when the
 * blacklist changes.
 */
export function useBlacklistDraft(params: {
  pickupLocationId?: number | string;
  serviceProviderId?: number;
}) {
  const { pickupLocationId, serviceProviderId } = params;

  const { data, isLoading } = useListBlacklistsQuery(
    {
      serviceProviderIds: serviceProviderId ? [BigInt(serviceProviderId)] : [],
      pickupLocationIds: pickupLocationId ? [BigInt(pickupLocationId)] : [],
    },
    // service_provider_ids is required by the backend's authorization check, and
    // without a location there is nothing to list.
    { skip: !pickupLocationId || !serviceProviderId },
  );

  const [createBlacklist] = useCreateBlacklistMutation();
  const [deleteBlacklist] = useDeleteBlacklistMutation();

  const serverRows: BlacklistDraftRow[] = useMemo(
    () =>
      (data?.blacklists ?? []).map((entry: ConnectProto.Blacklist) => ({
        key: String(entry.id),
        id: entry.id,
        truckTypeId: entry.truckTypeId,
        truckTypeName: entry.truckType?.name ?? String(entry.truckTypeId),
        reason: entry.reason ?? "",
        createdAt: entry.createdAt ? (fromTimestamp(entry.createdAt) ?? undefined) : undefined,
      })),
    [data],
  );

  // Draft overlay. `null` means "not edited yet", so a refetch keeps flowing
  // through until the dispatcher actually touches something.
  const [draft, setDraft] = useState<BlacklistDraftRow[] | null>(null);

  // The same draft as a ref: saving calls `flush` twice from one click, and the
  // second call carries a closure captured before the first ran, where `draft`
  // still looks dirty. A ref is shared across those closures, so the first call
  // can claim the draft and the second finds nothing to write.
  const draftRef = useRef<BlacklistDraftRow[] | null>(null);

  const rows = draft ?? serverRows;

  const applyDraft = useCallback((next: BlacklistDraftRow[] | null) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  // The modal is reused for every location the dispatcher steps through, so an
  // abandoned draft must not follow them to the next one — it would show, and on
  // save write, another location's truck types.
  useEffect(() => {
    applyDraft(null);
  }, [pickupLocationId, applyDraft]);

  const addTruckType = useCallback(
    (truckTypeId: bigint, truckTypeName: string, reason: string) => {
      const base = draftRef.current ?? serverRows;
      if (base.some((row) => row.truckTypeId === truckTypeId)) return;
      applyDraft([
        ...base,
        {
          key: `new-${truckTypeId}`,
          truckTypeId,
          truckTypeName,
          reason,
          createdAt: undefined,
        },
      ]);
    },
    [serverRows, applyDraft],
  );

  const removeRow = useCallback(
    (key: string) => {
      applyDraft((draftRef.current ?? serverRows).filter((row) => row.key !== key));
    },
    [serverRows, applyDraft],
  );

  const isDirty = draft !== null;

  /**
   * Applies the draft: an existing entry cannot be edited — the service has no
   * update RPC — so the diff is only removals and additions. Deletions run
   * before creations so that removing a truck type and re-adding it in one
   * session cannot collide with the unique (truck type, pickup location) pair
   * the backend enforces.
   */
  const flush = useCallback(async () => {
    const draftRows = draftRef.current;
    if (!draftRows || !pickupLocationId || !serviceProviderId) return;
    // Claimed before the first await, so the second call of the same save finds
    // nothing staged and issues no requests.
    draftRef.current = null;
    try {
      const kept = new Set(draftRows.filter((row) => row.id).map((row) => String(row.id)));
      const removed = serverRows.filter((row) => !kept.has(String(row.id)));
      const added = draftRows.filter((row) => !row.id);

      for (const row of removed) {
        await deleteBlacklist({ id: row.id! }).unwrap();
      }
      for (const row of added) {
        await createBlacklist({
          serviceProviderId: BigInt(serviceProviderId),
          pickupLocationId: BigInt(pickupLocationId),
          truckTypeId: row.truckTypeId,
          reason: row.reason || undefined,
        }).unwrap();
      }

      setDraft(null);
    } catch (error) {
      // Hand the draft back so the dispatcher can press Save again — some of
      // the batch may already have been applied, and the diff is recomputed
      // against the refetched list on the retry.
      draftRef.current = draftRows;
      throw error;
    }
  }, [pickupLocationId, serviceProviderId, serverRows, deleteBlacklist, createBlacklist]);

  return {
    rows,
    isLoading,
    isDirty,
    addTruckType,
    removeRow,
    flush,
  };
}
